import motion from '@lib/motion';
import { debounce, getObserver } from '@theme/theme-global';

class BannerShapes extends HTMLElement {
  static CONFIG = {
    TILT: {
      MAX_ANGLE: 20,
      SMOOTH_FACTOR: 0.12,
    },
    INTENSITY: {
      GOO: 1.2,
      VIDEO: 0.6,
    },
    ANIMATION: {
      LOAD_DELAY: 0,
      TRIANGLE_DURATION: 0.7,
      MASTER_DURATION: 2,
      FAN_DURATION: 0.9,
      FAN_DELAY: 0.35,
      VIDEO_DURATION: 0.55,
      VIDEO_DELAY: 1.65,
    },
    /* Keep in sync with --hero-scroll-screens / CSS height on .has-center-animated-media */
    SCROLL_SCREENS: 1.5,
  };

  constructor() {
    super();
    this.shape = this.dataset.shape;
    this.shapeWrapper = null;
    this.shape3d = null;
    this.media3d = null;
    this.mediaCard = null;

    // Animation state
    this.currentX = 0;
    this.currentY = 0;
    this.targetX = 0;
    this.targetY = 0;
    this.rafId = null;
    this.animationRun = false;
    this.scrollController = null;
    this._scrollTotal = 0;
    this._scrollProgress = null;
    this._scrollListening = false;
    this._introListening = false;
    this._viewportTarget = null;
    this._viewportObserver = null;

    // Bound methods for cleanup
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.updateTilt = this.updateTilt.bind(this);
    this.runInitialAnimation = this.runInitialAnimation.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleIntroScroll = this.handleIntroScroll.bind(this);
    this.handleResize = debounce(() => {
      this._scrollTotal = 0;
      this._scrollProgress = null;
      if (this._scrollListening) this.handleScroll();
    }, 150);
  }

  connectedCallback() {
    requestAnimationFrame(() => {
      this.init();
    });
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    this.cacheElements();
    if (this.enableShape) {
      if (!this.validateElements()) return;
    }
    this.setupMouseListeners();
    // CSS reserves runway via --hero-scroll-screens; clear any inline height so
    // innerHeight vs 100dvh never fights and CLS stays stable.
    if (this.scrollController) {
      this.scrollController.style.height = '';
    }
    this.setupViewportGate();
  }

  cacheElements() {
    this.shapeWrapper =
      this.querySelector('[data-diagonal-split-wrapper]') ||
      this.querySelector('[data-cross-shape-wrapper]') ||
      this.querySelector('[data-spiral-shape-wrapper]') ||
      this.querySelector('[data-triangle-shape-wrapper]');
    this.shape3d =
      this.querySelector('[data-diagonal-split-3d]') ||
      this.querySelector('[data-cross-shape-3d]') ||
      this.querySelector('[data-spiral-shape-3d]') ||
      this.querySelector('[data-triangle-shape-3d]');
    this.mediaCard = this.querySelector('[data-media-card]');
    this.media3d = this.querySelector('[data-media-3d]');
    this.hasAnimatedMedia = this.dataset.hasAnimatedMedia === 'true';
    this.scrollController = this.closest('.hero-scroll-controller');
    this.enableShape = this.dataset.enableShape === 'true';
  }

  validateElements() {
    const required = ['shapeWrapper', 'shape3d'];
    const missing = required.filter((key) => !this[key]);

    if (missing.length > 0) {
      console.warn(`BannerShapes: Missing elements:`, missing);
      return false;
    }

    return true;
  }

  validateElements1() {
    const required = ['shapeWrapper', 'shape3d'];
    if (this.hasAnimatedMedia) {
      required.push('mediaCard', 'media3d');
    }
    const missing = required.filter((key) => !this[key]);

    if (missing.length > 0) {
      console.warn(`BannerShapes: Missing elements:`, missing);
      return false;
    }
    return true;
  }

  setupViewportGate() {
    const isFirstSection = this.dataset.index === '1';
    if (isFirstSection) this.runInitialAnimation();

    const needsIntroGate = !isFirstSection && !this.animationRun;
    const needsMediaScroll = this.hasAnimatedMedia && !!this.media3d;

    if (!needsIntroGate && !needsMediaScroll) return;

    this._viewportTarget = this.scrollController || this.parentElement || this;

    const onNear = () => {
      if (needsMediaScroll) this.bindMediaScroll();
      if (needsMediaScroll) this.handleScroll();
      if (!this.animationRun && this.dataset.index !== '1') {
        this.bindIntroScroll();
        this.handleIntroScroll();
      }
    };

    const onFar = () => {
      this.unbindMediaScroll();
      this.unbindIntroScroll();
    };

    if ('IntersectionObserver' in window) {
      this._viewportObserver = getObserver({ rootMargin: '300px 0px', threshold: 0 }, false, true);
      this._viewportObserver.observe(this._viewportTarget, (entry) => {
        if (entry.isIntersecting) onNear();
        else onFar();
      });
    } else {
      onNear();
      if (needsIntroGate) {
        requestAnimationFrame(() => this.handleIntroScroll());
      }
    }

    if (needsMediaScroll) {
      window.addEventListener('resize', this.handleResize, { passive: true });
    }
  }

  bindIntroScroll() {
    if (this._introListening || this.animationRun) return;
    this._introListening = true;
    window.addEventListener('scroll', this.handleIntroScroll, { passive: true });
  }

  unbindIntroScroll() {
    if (!this._introListening) return;
    this._introListening = false;
    window.removeEventListener('scroll', this.handleIntroScroll);
  }

  bindMediaScroll() {
    if (this._scrollListening || !this.media3d) return;
    this._scrollListening = true;
    this.cacheScrollMetrics();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  unbindMediaScroll() {
    if (!this._scrollListening) return;
    this._scrollListening = false;
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleIntroScroll() {
    if (this.animationRun) return;

    // Using scroll controller (section wrapper), not sticky banner-shapes.
    // With center media, banner-shapes is sticky at --stickyOffset (~2rem),
    // so its top stays > 0 until sticky ends — intro would fire too late.
    const target = this.scrollController || this;
    if (target.getBoundingClientRect().top > 0) return;

    this.runInitialAnimation();
  }

  runInitialAnimation() {
    if (this.animationRun) return; // Prevent multiple runs
    this.animationRun = true;
    this.unbindIntroScroll();

    requestAnimationFrame(() => {
      if (this.enableShape) {
        if (this.shape === 'diagonal-split') {
          this.animateDiagonalShape();
        } else if (this.shape === 'triangles-cross') {
          this.animateCrossShape();
        } else if (this.shape === 'spiral') {
          this.animateSpiralShape();
        } else if (this.shape === 'triangle') {
          this.animateTriangleShape();
        }
      }

      if (this.hasAnimatedMedia && this.mediaCard) {
        this.animateVideo();
      }
    });
  }

  animateDiagonalShape() {
    this.animateMasterTransform();
    this.animateFan();
  }

  animateCrossShape() {
    this.animateCrossScale();
    this.animateCrossRotate();
  }

  animateSpiralShape() {
    this.animateSpiralScaleAndRotate();
  }

  animateMasterTransform() {
    const { ANIMATION } = BannerShapes.CONFIG;

    motion.animate(
      this.shapeWrapper,
      {
        transform: [
          'translate(-50%, -50%) scale(0.08) rotate(-150deg)',
          'translate(-50%, -50%) scale(1.18) rotate(-150deg)',
          'translate(-50%, -50%) scale(1.18) rotate(30deg)',
        ],
      },
      {
        duration: ANIMATION.MASTER_DURATION,
        times: [0, 0.6, 1],
        easing: [
          [0.8, 0, 0.2, 1],
          [0.7, 0, 0.2, 1],
        ],
      },
    );
  }

  animateFan() {
    const { ANIMATION } = BannerShapes.CONFIG;

    motion.animate(
      this.shapeWrapper,
      {
        '--poLeft': ['75%', '165%'],
        '--poRight': ['25%', '-85%'],
      },
      {
        duration: ANIMATION.FAN_DURATION,
        delay: ANIMATION.FAN_DELAY,
        easing: [0.9, 0, 0.1, 1],
      },
    );
  }

  animateSpiralScaleAndRotate() {
    const { ANIMATION } = BannerShapes.CONFIG;

    motion.animate(
      this.shapeWrapper,
      {
        transform: [
          'scale(0.08) rotate(0deg)',
          'scale(0.513) rotate(70deg)',
          'scale(0.946) rotate(140deg)',
          'scale(1.378) rotate(210deg)',
        ],
      },
      {
        duration: ANIMATION.MASTER_DURATION,
        easing: 'linear',
        times: [0, 0.333, 0.666, 1],
      },
    );
  }

  animateCrossScale() {
    const { ANIMATION } = BannerShapes.CONFIG;

    motion.animate(
      this.shapeWrapper,
      {
        transform: ['scale(0.08)', 'scale(2.5)'],
      },
      {
        duration: ANIMATION.MASTER_DURATION,
        easing: [0.8, 0, 0.2, 1],
      },
    );
  }

  animateCrossRotate() {
    motion.animate(
      this.shape3d,
      {
        '--rotate': ['44deg', '224deg', '210deg'],
      },
      {
        duration: BannerShapes.CONFIG.ANIMATION.FAN_DURATION,
        delay: BannerShapes.CONFIG.ANIMATION.FAN_DELAY,
        easing: [0.8, 0, 0.2, 1],
      },
    );
  }

  animateTriangleShape() {
    const { ANIMATION } = BannerShapes.CONFIG;
    motion.animate(
      this.shapeWrapper,
      {
        '--valA': ['0%', '100%'],
        '--valB': ['100%', '15%'],
        '--valC': ['0%', '100%'],
        '--valD': ['100%', '100%'],
      },
      {
        duration: ANIMATION.TRIANGLE_DURATION,
        easing: [0.8, 0, 0.2, 1],
      },
    );
  }

  animateVideo() {
    const { ANIMATION } = BannerShapes.CONFIG;
    // Delay only when shapes run first; without shapes, play immediately on enter
    const delay = this.enableShape ? ANIMATION.VIDEO_DELAY : 0;
    motion.animate(
      this.mediaCard,
      {
        transform: ['scale(0)', 'scale(0.6)', 'scale(1)'],
      },
      {
        duration: ANIMATION.VIDEO_DURATION,
        delay,
        times: [0, 0.4, 1],
        easing: [0.2, 1, 0.3, 1],
      },
    );
  }

  setupMouseListeners() {
    this.addEventListener('mousemove', this.handleMouseMove);
    this.addEventListener('mouseleave', this.handleMouseLeave);
  }

  handleMouseMove(e) {
    const rect = this.getBoundingClientRect();

    this.targetX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    this.targetY = ((e.clientY - rect.top) / rect.height - 0.5) * 2;

    if (!this.rafId) {
      this.rafId = requestAnimationFrame(() => this.updateTilt());
    }
  }

  handleMouseLeave() {
    this.targetX = 0;
    this.targetY = 0;

    if (!this.rafId) {
      this.rafId = requestAnimationFrame(() => this.updateTilt());
    }
  }

  updateTilt() {
    const { TILT, INTENSITY } = BannerShapes.CONFIG;

    this.currentX = this.lerp(this.currentX, this.targetX, TILT.SMOOTH_FACTOR);
    this.currentY = this.lerp(this.currentY, this.targetY, TILT.SMOOTH_FACTOR);

    const rx = this.currentY * TILT.MAX_ANGLE;
    const ry = this.currentX * TILT.MAX_ANGLE;

    // Apply GOO tilt (stronger)
    if (this.enableShape) {
      this.shape3d.style.setProperty('--rx', `${rx * INTENSITY.GOO}deg`);
      this.shape3d.style.setProperty('--ry', `${ry * INTENSITY.GOO}deg`);
    }

    if (this.hasAnimatedMedia && this.media3d) {
      // Apply VIDEO tilt (softer + opposite)
      this.media3d.style.setProperty('--vrx', `${-rx * INTENSITY.VIDEO}deg`);
      this.media3d.style.setProperty('--vry', `${-ry * INTENSITY.VIDEO}deg`);
    }
    // Stop RAF when stabilized (performance)
    const isStabilized =
      Math.abs(this.currentX - this.targetX) < 0.001 && Math.abs(this.currentY - this.targetY) < 0.001;

    if (isStabilized) {
      this.rafId = null;
      return;
    }

    this.rafId = requestAnimationFrame(() => this.updateTilt());
  }

  cacheScrollMetrics() {
    const container = this.scrollController || this.parentElement;
    if (!container) {
      this._scrollTotal = 0;
      return;
    }

    const total = container.getBoundingClientRect().height - window.innerHeight;
    this._scrollTotal = total > 0 ? total : 0;
  }

  handleScroll() {
    if (!this.media3d) return;

    const container = this.scrollController || this.parentElement;
    if (!container) return;

    if (!(this._scrollTotal > 0)) {
      this.cacheScrollMetrics();
      if (!(this._scrollTotal > 0)) return;
    }

    const top = container.getBoundingClientRect().top;
    const progress = Math.min(Math.max(-top / this._scrollTotal, 0), 1);

    if (progress === this._scrollProgress) return;
    this._scrollProgress = progress;

    const width = 25 + progress * 75;
    const height = 30 + progress * 70;
    const scale = 1 + progress * 0.1;

    this.media3d.style.setProperty('--mediaWidth', `${width}%`);
    this.media3d.style.setProperty('--mediaHeight', `${height}vh`);
    this.media3d.style.setProperty('--vscale', scale);
  }

  lerp(a, b, t) {
    return a + (b - a) * t;
  }

  cleanup() {
    // Cancel RAF
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.unbindIntroScroll();
    this.unbindMediaScroll();
    window.removeEventListener('resize', this.handleResize);

    if (this._viewportObserver && this._viewportTarget) {
      this._viewportObserver.unobserve(this._viewportTarget);
    }
    this._viewportObserver = null;
    this._viewportTarget = null;

    // Remove event listeners
    this.removeEventListener('mousemove', this.handleMouseMove);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    // Cleanup references
    this.shapeWrapper = null;
    this.shape3d = null;
    this.media3d = null;
    this.mediaCard = null;
    this.scrollController = null;
    // Reset state
    this.currentX = 0;
    this.currentY = 0;
    this.targetX = 0;
    this.targetY = 0;
  }
}

if (!customElements.get('banner-shapes')) {
  customElements.define('banner-shapes', BannerShapes);
}
